import { useState, forwardRef, Ref, useEffect } from 'react';
import { cn } from '../../../utils';
import { AcceptsSetFormItemError } from '../FormItem/FormItem';

type CheckboxOptions = {
    id: string;
    value: string;
    description?: string;
    label: string;
    disabled: boolean;
};

export type CheckboxGroupProps = {
    options: CheckboxOptions[];
    className?: string | object;
    legendText?: string;
    name: string;
    onChange?: (e: string[]) => void;
    error?: string;
    defaultValues?: string[];
} & AcceptsSetFormItemError;

export default forwardRef(function CheckboxGroup(
    {
        className,
        options,
        legendText,
        name,
        error,
        defaultValues = [],
        onChange,
    }: CheckboxGroupProps,
    ref: Ref<HTMLInputElement>,
) {
    const [value, setValue] = useState<string[]>(defaultValues);
    useEffect(() => {
        onChange && onChange(value);
    }, [value, defaultValues]);

    return (
        <fieldset>
            {legendText && (
                <legend className="alli-sr-only">{legendText}</legend>
            )}
            <div className="alli-space-y-5">
                {options.map(option => {
                    return (
                        <div
                            data-testid={`checkbox-option-${option.id}`}
                            key={`checkbox-option-${option.id}`}
                            className={cn(
                                'alli-relative alli-flex alli-items-start',
                                {
                                    'alli-cursor-not-allowed': option.disabled,
                                },
                                className,
                            )}>
                            <div className="alli-flex alli-h-6 alli-items-center">
                                <input
                                    onChange={() => {
                                        setValue(prev => {
                                            if (prev.includes(option.value)) {
                                                const index = prev.indexOf(
                                                    option.value,
                                                );
                                                if (index > -1) {
                                                    prev.splice(index, 1);
                                                }
                                                return [...prev];
                                            } else {
                                                return [...prev, option.value];
                                            }
                                        });
                                    }}
                                    id={option.id}
                                    aria-describedby={`${option.id}-description`}
                                    name={`${name}[]`}
                                    type="checkbox"
                                    disabled={option.disabled}
                                    checked={value.includes(option.value)}
                                    className={cn(
                                        'alli-h-4 alli-w-4 alli-rounded alli-border-gray-700 alli-text-blue-600 focus:alli-ring-blue-600',
                                        {
                                            'alli-border-gray-400':
                                                option.disabled,
                                            'alli-text-gray-600':
                                                option.disabled &&
                                                value.includes(option.value),
                                            'alli-border-red-400': error,
                                        },
                                    )}
                                    ref={ref}
                                />
                            </div>
                            <div className="alli-ml-3 alli-text-sm alli-leading-6">
                                <label
                                    htmlFor={option.id}
                                    className={cn(
                                        'alli-select-none alli-font-medium alli-text-blue-gray-700',
                                        {
                                            'alli-text-gray-600':
                                                option.disabled,
                                        },
                                    )}>
                                    {option.label}
                                </label>
                                {option?.description && (
                                    <p
                                        id={`${option.id}-description`}
                                        className={cn('alli-text-gray-750', {
                                            'alli-text-gray-600':
                                                option.disabled,
                                        })}>
                                        {option.description}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </fieldset>
    );
});
